import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { CustomerModal } from "@lib/components/PageComponents/Customer";
import { Customer } from "@lib/types/Customer";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { SharedContextProps } from "@lib/types/Context";
import { useApi } from "@lib/hooks/APIHooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiAddCustomer,
  apiDeleteCustomer,
  apiGetCustomers,
  apiUpdateCustomer,
} from "@lib/api/customer";
import { toast } from "react-toastify";

const SharedCustomers: React.FC<SharedContextProps> = ({
  context,
  customTitle,
  minusHeight,
  pageIsActive = true,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { queriesCacheClear } = useApi();

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const queryKeys = ["customer", JSON.stringify(params)];

  const customersQuery = useQuery({
    queryFn: () => apiGetCustomers(params),
    queryKey: queryKeys,
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = customersQuery;
  const apiData = data?.data?.data;

  const addCustomerMutation = useMutation({
    mutationFn: apiAddCustomer,
    mutationKey: ["customer"],
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.customer"] as string,
        ),
      );
      queriesCacheClear({ search: "customer" });
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: apiUpdateCustomer,
    mutationKey: ["customer"],
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.customer"] as string,
        ),
      );
      queriesCacheClear({ search: "customer" });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: apiDeleteCustomer,
    mutationKey: ["customer"],
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.customer"] as string,
        ),
      );
      queriesCacheClear({ search: "customer" });
    },
  });

  useEffect(() => {
    if (
      addCustomerMutation.isSuccess ||
      updateCustomerMutation.isSuccess ||
      deleteCustomerMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addCustomerMutation.isSuccess,
    updateCustomerMutation.isSuccess,
    deleteCustomerMutation.isSuccess,
  ]);

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Customer>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelected(customer);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        handleSelectCustomer,
        navigate,
        deleteQuery: deleteCustomerMutation,
        openModal,
        apiData,
        params,
        messages,
      }),
    [apiData, deleteCustomerMutation, openModal, handleSelectCustomer, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params?.filter]);

  const title = customTitle || (messages["whiteLabel.list"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={customersQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
          minusHeight={minusHeight}
        />
      </AppsContent>

      <CustomerModal
        mutationQuery={selected ? updateCustomerMutation : addCustomerMutation}
        openModal={openModal}
        toggleModal={toggle}
        customer={selected}
      />
    </AppsContainer>
  );
};

export default SharedCustomers;
